import React, { useMemo, useState } from "react";
import { GripVertical, ChevronRight, ChevronDown, X, Download, Bookmark, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { ExpenseDrillModal } from "@/components/ExpenseDrillModal";

const MN = ["Ιαν", "Φεβ", "Μαρ", "Απρ", "Μάι", "Ιουν", "Ιουλ", "Αυγ", "Σεπ", "Οκτ", "Νοε", "Δεκ"];
const FIELDS = {
  cat: { label: "Κατηγορία SoftOne", get: (d) => d.cat || "Χωρίς κατηγορία" },
  supplier: { label: "Προμηθευτής", get: (d) => d.supplier },
  line: { label: "Γραμμή P&L", get: (d, lines) => (d.eff_counted ? (d.eff_pl ? `${d.eff_pl} ${lines.find((l) => l.code === d.eff_pl)?.label || ""}` : "Χωρίς γραμμή P&L") : "Αγνοούνται") },
  type: { label: "Τύπος παραστατικού", get: (d) => d.doc_type || "—" },
  month: { label: "Μήνας", get: (d) => MN[new Date(d.date).getMonth()], order: (a, b) => MN.indexOf(a) - MN.indexOf(b) },
  counted: { label: "Μετράει", get: (d) => (d.eff_counted ? "Μετράει" : "Αγνοείται") },
};
const EDITABLE = { cat: "category", supplier: "supplier" };
const STORE = "exp-pivot-layout";
const SAVED = "exp-pivot-saved";
const DEFAULT = { rows: ["cat", "supplier"], col: null };
const load = (k) => { try { return JSON.parse(localStorage.getItem(k)) || null; } catch { return null; } };

const Chip = ({ id, onRemove, testid }) => (
  <span draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", id)} data-testid={testid}
    className="inline-flex items-center gap-1 rounded-lg border border-[#E3DED4] bg-white px-2 py-1 text-xs font-semibold text-[#45423C] cursor-grab active:cursor-grabbing select-none hover:border-[#45423C]">
    <GripVertical className="h-3 w-3 text-[#B9AE9A]" />{FIELDS[id].label}
    {onRemove && <button onClick={() => onRemove(id)} className="ml-1 text-[#8A8577] hover:text-[#B4412A]" data-testid={`${testid}-remove`}><X className="h-3 w-3" /></button>}
  </span>
);

const Zone = ({ title, hint, ids, onDrop, onRemove, testid, max }) => {
  const [over, setOver] = useState(false);
  return (
    <div data-testid={testid} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(e.dataTransfer.getData("text/plain")); }}
      className={`flex-1 min-w-[220px] rounded-xl border-2 border-dashed p-2 min-h-[52px] transition-colors ${over ? "border-[#C86235] bg-[#FDF6F1]" : "border-[#E3DED4] bg-[#FAF8F4]"}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#6F6A60] mb-1">{title}{max ? ` (έως ${max})` : ""}</div>
      <div className="flex flex-wrap gap-1.5">
        {ids.length === 0 && <span className="text-xs text-[#B9AE9A]">{hint}</span>}
        {ids.map((id) => <Chip key={id} id={id} onRemove={onRemove} testid={`${testid}-${id}`} />)}
      </div>
    </div>
  );
};

const build = (docs, rows, col, lines) => {
  const colKeys = new Set();
  const mk = (name) => ({ name, docs: 0, total: 0, counted: 0, nCounted: 0, pls: new Set(), cells: {}, children: {} });
  const root = mk("root");
  docs.forEach((d) => {
    const ck = col ? FIELDS[col].get(d, lines) : null;
    if (ck !== null) colKeys.add(ck);
    let node = root;
    const add = (n) => { n.docs += 1; n.total += d.net; if (d.eff_counted) { n.counted += d.net; n.nCounted += 1; } n.pls.add(d.eff_pl || ""); if (ck !== null) n.cells[ck] = (n.cells[ck] || 0) + d.net; };
    add(node);
    rows.forEach((f) => { const k = FIELDS[f].get(d, lines); node = node.children[k] || (node.children[k] = mk(k)); add(node); });
  });
  const cols = Array.from(colKeys).sort(FIELDS[col]?.order || ((a, b) => String(a).localeCompare(String(b), "el")));
  return { root, cols };
};

const SavedLayouts = ({ current, apply }) => {
  const [list, setList] = useState(() => load(SAVED) || []);
  const [name, setName] = useState("");
  const persist = (l) => { setList(l); localStorage.setItem(SAVED, JSON.stringify(l)); };
  const save = () => { const n = name.trim(); if (!n) return; persist([...list.filter((x) => x.name !== n), { name: n, ...current }]); setName(""); toast.success(`Αποθηκεύτηκε η διάταξη «${n}»`); };
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="exp-pivot-saved">
      <Bookmark className="h-3.5 w-3.5 text-[#C86235]" />
      {list.map((l) => (
        <span key={l.name} className="inline-flex items-center rounded-lg border border-[#E3DED4] bg-white overflow-hidden">
          <button data-testid={`exp-pivot-layout-${l.name}`} onClick={() => apply(l.rows, l.col)} className="px-2 py-1 font-semibold text-[#45423C] hover:bg-[#FAF8F4]">{l.name}</button>
          <button data-testid={`exp-pivot-layout-del-${l.name}`} onClick={() => persist(list.filter((x) => x.name !== l.name))} className="px-1.5 py-1 text-[#8A8577] hover:text-[#B4412A] border-l border-[#E3DED4]"><Trash2 className="h-3 w-3" /></button>
        </span>
      ))}
      <input data-testid="exp-pivot-layout-name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder="Όνομα διάταξης…" className="rounded-lg border border-[#E3DED4] bg-white px-2 py-1 outline-none focus:border-[#45423C] w-40" />
      <button data-testid="exp-pivot-layout-save" onClick={save} disabled={!name.trim()} className="rounded-lg bg-[#45423C] text-white px-2.5 py-1 font-semibold disabled:opacity-40">Αποθήκευση</button>
    </div>
  );
};

export const ExpensePivot = ({ docs, prevDocs = [], year, lines, canEdit, onRule, busy }) => {
  const saved = load(STORE);
  const [rows, setRows] = useState(saved?.rows || DEFAULT.rows);
  const [col, setCol] = useState(saved?.col ?? null);
  const [compare, setCompare] = useState(saved?.compare ?? false);
  const [open, setOpen] = useState({});
  const [sortDesc, setSortDesc] = useState(true);
  const persist = (r, c, cmp = compare) => { setRows(r); setCol(c); setCompare(cmp); localStorage.setItem(STORE, JSON.stringify({ rows: r, col: c, compare: cmp })); };
  const avail = Object.keys(FIELDS).filter((f) => !rows.includes(f) && f !== col);
  const toRows = (id) => { if (!FIELDS[id]) return; persist([...rows.filter((r) => r !== id), id], col === id ? null : col); };
  const toCol = (id) => { if (!FIELDS[id]) return; persist(rows.filter((r) => r !== id), id); };
  const toAvail = (id) => { if (!FIELDS[id]) return; persist(rows.filter((r) => r !== id), col === id ? null : col); };
  const { root, cols } = useMemo(() => build(docs, rows, col, lines), [docs, rows, col, lines]);
  const prevRoot = useMemo(() => (compare ? build(prevDocs, rows, col, lines).root : null), [prevDocs, rows, col, lines, compare]);
  const prevOf = (path) => { let n = prevRoot; for (const k of path.split("/").filter(Boolean)) { n = n?.children[k]; if (!n) return null; } return n; };
  const pct = (cur, prev) => (prev ? Math.round(((cur - prev) / Math.abs(prev)) * 1000) / 10 : null);
  const showCmp = compare && !col;
  const colCmp = compare && !!col;
  const sortKids = (n) => Object.values(n.children).sort((a, b) => (sortDesc ? b.total - a.total : a.total - b.total));
  const toggle = (p) => setOpen((o) => ({ ...o, [p]: !o[p] }));
  const setAll = (v) => { const o = {}; const walk = (n, p) => Object.values(n.children).forEach((c) => { o[`${p}/${c.name}`] = v; walk(c, `${p}/${c.name}`); }); walk(root, ""); setOpen(o); };
  const nCols = (col ? cols.length : 1) + (showCmp ? 2 : 0);
  const headers = [rows.map((r) => FIELDS[r].label).join(" / ") || "Σύνολο", "Παραστ.", ...(col ? cols : ["Μετράει €"]), "Σύνολο €", ...(showCmp ? [`Πέρυσι (${year - 1}) €`, "Διαφορά %"] : [])];
  const vals = (n, path) => {
    const base = [...(col ? cols.map((k) => n.cells[k] || 0) : [n.counted]), n.total];
    if (showCmp) { const p = path === null ? prevRoot : prevOf(path); const pv = p?.total || 0; base.push(pv, pct(n.total, pv) ?? 0); }
    return base.map((v) => Math.round(v * 100) / 100);
  };
  const exportXlsx = async () => {
    const out = [];
    const walk = (n, depth, path) => sortKids(n).forEach((c) => { const p = `${path}/${c.name}`; out.push({ name: c.name, depth, docs: c.docs, values: vals(c, p) }); walk(c, depth + 1, p); });
    walk(root, 0, "");
    out.push({ name: "ΣΥΝΟΛΟ", depth: 0, docs: root.docs, values: vals(root, null), is_total: true });
    const res = await api.post("/expenses/pivot.xlsx", { headers, rows: out }, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "expenses_pivot.xlsx"; a.click(); URL.revokeObjectURL(url);
  };
  const [drill, setDrill] = useState(null);
  const filterDocs = (keys, k) => docs.filter((d) => keys.every((kv, i) => FIELDS[rows[i]].get(d, lines) === kv) && (k === null || !col || FIELDS[col].get(d, lines) === k));
  const drillDocs = (path, k) => {
    const keys = path ? path.split("/").filter(Boolean) : [];
    setDrill({ title: `${keys.join(" › ") || "Όλα"}${k !== null && col ? ` · ${k}` : " · Σύνολο"}`, keys, k });
  };
  const cell = (v, bold, path, k = null) => <td onClick={path !== undefined ? (e) => { e.stopPropagation(); drillDocs(path, k); } : undefined} className={`px-3 py-1.5 text-right tabular whitespace-nowrap ${bold ? "font-semibold" : ""} ${v < 0 ? "text-[#6E7A63]" : ""} ${path !== undefined ? "cursor-pointer hover:bg-[#FBF4EC] hover:text-[#C86235]" : ""}`}>{v ? fmtEuro(v) : <span className="text-[#D6D0C4]">·</span>}</td>;
  const heat = (d) => (d === null ? "" : d > 25 ? "bg-[#F8DCD3] text-[#8E2F1C]" : d > 5 ? "bg-[#FBEFE9]" : d < -25 ? "bg-[#D9E6D2] text-[#2F5A26]" : d < -5 ? "bg-[#EEF4EA]" : "");
  const colCell = (v, prevNode, k, bold, path) => {
    if (!colCmp) return cell(v, bold, path, k === "__t" ? null : k);
    const pv = prevNode?.cells[k] || 0; const d = pct(v, pv);
    return (
      <td key={k} onClick={(e) => { e.stopPropagation(); drillDocs(path, k === "__t" ? null : k); }} title={`${year - 1}: ${fmtEuro(pv)}${d !== null ? ` · ${d > 0 ? "+" : ""}${d}%` : pv || v ? " · νέο" : ""}`}
        className={`px-3 py-1 text-right tabular whitespace-nowrap cursor-pointer hover:text-[#C86235] ${bold ? "font-semibold" : ""} ${heat(d)}`}>
        {v ? fmtEuro(v) : <span className="text-[#D6D0C4]">·</span>}
        {(v || pv) ? <div className="text-[9px] font-normal leading-tight opacity-70">{fmtEuro(pv)}{d !== null ? ` ${d > 0 ? "+" : ""}${d}%` : ""}</div> : null}
      </td>
    );
  };
  const cmpCells = (n, path) => {
    if (!showCmp) return null;
    const pv = (path === null ? prevRoot : prevOf(path))?.total || 0; const d = pct(n.total, pv);
    return (<>
      {cell(pv, false)}
      <td className={`px-3 py-1.5 text-right tabular whitespace-nowrap text-xs font-semibold ${d === null ? "text-[#B9AE9A]" : d > 0 ? "text-[#B4412A]" : "text-[#4A7A3E]"}`}>{d === null ? (n.total ? "νέο" : "·") : `${d > 0 ? "+" : ""}${d.toLocaleString("el-GR")}%`}</td>
    </>);
  };
  const editor = (c, field) => {
    const level = EDITABLE[field];
    if (!level || !canEdit) return null;
    const pl = c.pls.size === 1 ? [...c.pls][0] : "";
    const on = c.nCounted === c.docs ? true : c.nCounted === 0 ? false : null;
    return (
      <span className="inline-flex items-center gap-2 ml-3" onClick={(e) => e.stopPropagation()}>
        <select data-testid={`exp-pivot-line-${c.name}`} disabled={busy} value={pl} onChange={(e) => onRule({ level, key: c.name, pl_code: e.target.value })}
          className={`rounded-lg border bg-white px-1.5 py-0.5 text-[11px] outline-none focus:border-[#45423C] max-w-[220px] ${!pl ? "border-[#F0C36D] bg-[#FFFBEB]" : "border-[#E3DED4]"}`}>
          <option value="">{c.pls.size > 1 ? "— μικτές γραμμές —" : "— χωρίς γραμμή —"}</option>
          {lines.map((l) => <option key={l.code} value={l.code}>{l.code} {l.label}</option>)}
        </select>
        <button type="button" data-testid={`exp-pivot-toggle-${c.name}`} disabled={busy} onClick={() => onRule({ level, key: c.name, counted: on !== true })}
          className={`relative inline-flex h-4 w-8 items-center rounded-full disabled:opacity-40 ${on === true ? "bg-[#6E7A63]" : on === false ? "bg-[#B9AE9A]" : "bg-[#E3B23C]"}`} title={on === null ? "Μικτό: κάποια μετράνε" : on ? "Μετράει" : "Αγνοείται"}>
          <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transition-transform ${on === false ? "translate-x-0.5" : on === true ? "translate-x-4" : "translate-x-2"}`} />
        </button>
      </span>
    );
  };
  const render = (n, depth, path) => sortKids(n).map((c) => {
    const p = `${path}/${c.name}`; const leaf = depth === rows.length - 1; const isOpen = !!open[p];
    return (
      <React.Fragment key={p}>
        <tr data-testid={`exp-pivot-row-${c.name}`} onClick={() => !leaf && toggle(p)} className={`border-t border-[#F1EDE6] ${leaf ? "" : "cursor-pointer hover:bg-[#FAF8F4]"} ${depth === 0 ? "bg-[#FCFBF8]" : ""} ${c.nCounted === 0 ? "opacity-60" : ""}`}>
          <td className="px-3 py-1.5" style={{ paddingLeft: 12 + depth * 20 }}>
            <span className={`inline-flex items-center gap-1.5 ${depth === 0 ? "font-semibold text-[#45423C]" : "text-[#45423C]"}`}>
              {!leaf ? (isOpen ? <ChevronDown className="h-3.5 w-3.5 text-[#C86235]" /> : <ChevronRight className="h-3.5 w-3.5 text-[#B9AE9A]" />) : <span className="w-3.5" />}
              <span className="max-w-[300px] truncate" title={c.name}>{c.name}</span>
            </span>
            {editor(c, rows[depth])}
          </td>
          <td className="px-3 text-right tabular text-[#6F6A60]">{c.docs}</td>
          {col ? cols.map((k) => <React.Fragment key={k}>{colCell(c.cells[k] || 0, colCmp ? prevOf(p) : null, k, depth === 0, p)}</React.Fragment>) : cell(c.counted, depth === 0, p)}
          {colCmp ? colCell(c.total, prevOf(p) && { cells: { __t: prevOf(p).total } }, "__t", true, p) : cell(c.total, true, p)}
          {cmpCells(c, p)}
        </tr>
        {!leaf && isOpen && render(c, depth + 1, p)}
      </React.Fragment>
    );
  });

  return (
    <div className="space-y-3" data-testid="exp-pivot">
      <div className="rounded-2xl bg-white border border-[#E3DED4] p-3 space-y-2">
        <p className="text-xs text-[#6F6A60]">Σύρετε τα πεδία στις «Γραμμές» με τη σειρά που θέλετε να ομαδοποιούνται, ή ένα πεδίο στις «Στήλες» για διασταύρωση (π.χ. Μήνας). Σε γραμμές Κατηγορίας/Προμηθευτή αλλάζετε γραμμή P&L και «Μετράει» επιτόπου.</p>
        <div className="flex flex-wrap gap-2">
          <Zone title="Διαθέσιμα πεδία" hint="—" ids={avail} onDrop={toAvail} testid="exp-pivot-avail" />
          <Zone title="Γραμμές (σειρά)" hint="Σύρετε εδώ" ids={rows} onDrop={toRows} onRemove={toAvail} testid="exp-pivot-rows" />
          <Zone title="Στήλες" hint="Σύρετε ένα πεδίο" ids={col ? [col] : []} onDrop={toCol} onRemove={toAvail} testid="exp-pivot-col" max={1} />
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <button data-testid="exp-pivot-expand" onClick={() => setAll(true)} className="rounded-lg border border-[#E3DED4] px-2 py-1 hover:border-[#45423C]">Άνοιγμα όλων</button>
          <button data-testid="exp-pivot-collapse" onClick={() => setAll(false)} className="rounded-lg border border-[#E3DED4] px-2 py-1 hover:border-[#45423C]">Κλείσιμο όλων</button>
          <button data-testid="exp-pivot-sort" onClick={() => setSortDesc(!sortDesc)} className="rounded-lg border border-[#E3DED4] px-2 py-1 hover:border-[#45423C]">Ταξινόμηση: {sortDesc ? "μεγαλύτερο πρώτα" : "μικρότερο πρώτα"}</button>
          <button data-testid="exp-pivot-reset" onClick={() => { persist(DEFAULT.rows, null); setOpen({}); }} className="rounded-lg border border-[#E3DED4] px-2 py-1 text-[#8A8577] hover:border-[#45423C]">Επαναφορά διάταξης</button>
          <button data-testid="exp-pivot-compare" onClick={() => persist(rows, col, !compare)} title={col ? "Στις στήλες: χρώμα απόκλισης ανά κελί, πέρυσι από κάτω" : ""} className={`rounded-lg border px-2 py-1 font-semibold ${compare ? "bg-[#45423C] text-white border-[#45423C]" : "border-[#E3DED4] hover:border-[#45423C]"}`}>Σύγκριση με {year - 1}</button>
          <button data-testid="exp-pivot-export" onClick={() => exportXlsx().catch(() => toast.error("Αποτυχία εξαγωγής"))} className="inline-flex items-center gap-1 rounded-lg border border-[#E3DED4] px-2 py-1 font-semibold hover:border-[#45423C]"><Download className="h-3.5 w-3.5" /> Excel (με υποσύνολα)</button>
        </div>
        <SavedLayouts current={{ rows, col }} apply={(r, c) => { persist(r, c); setOpen({}); }} />
      </div>
      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60]">
            <tr>{headers.map((h, i) => <th key={i} className={`px-3 py-2 whitespace-nowrap ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={3 + nCols} className="px-4 py-6 text-center text-[#6F6A60]">Σύρετε τουλάχιστον ένα πεδίο στις «Γραμμές».</td></tr>}
            {render(root, 0, "")}
            <tr className="border-t-2 border-[#45423C] bg-[#F3F0EA] font-semibold" data-testid="exp-pivot-total">
              <td className="px-3 py-2">ΣΥΝΟΛΟ</td><td className="px-3 text-right tabular">{root.docs}</td>
              {col ? cols.map((k) => <React.Fragment key={k}>{colCell(root.cells[k] || 0, prevRoot, k, true, "")}</React.Fragment>) : cell(root.counted, true, "")}
              {colCmp ? colCell(root.total, prevRoot && { cells: { __t: prevRoot.total } }, "__t", true, "") : cell(root.total, true, "")}
              {cmpCells(root, null)}
            </tr>
          </tbody>
        </table>
      </div>
      {drill && <ExpenseDrillModal drill={{ ...drill, docs: filterDocs(drill.keys, drill.k) }} lines={lines} canEdit={canEdit} busy={busy} onRule={onRule} onClose={() => setDrill(null)} />}
    </div>
  );
};
